import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { useBranchScope } from '@/hooks/useBranchScope';
import { useT } from '@/contexts/I18nContext';
import {
  getPortfolioDashboard,
  getPortfolioSummary,
  type PortfolioZoneDashboardDto,
  type PortfolioZoneDto,
} from '@/api/portfolio';
import { isRetailZone } from '@/lib/zoneComponents';
import { rankZones, ZoneHealthCard } from './ZoneHealthCard';
import { ZoneDashboardCard } from './ZoneDashboardCard';
import { ShowMoreZonesButton, ZONES_PAGE_SIZE } from './usePagedZones';

// «Все филиалы» live dashboard. Pagination is DATA-LEVEL, not just visual:
// the cheap coarse summary (/core/api/portfolio/, 90s server cache) provides
// the full ranked branch list; the heavy per-unit batch is then fetched in
// pages of ≤20 zone_ids — the BE never computes zones the user isn't looking
// at (2026-07-19 incident: unscoped batch ran 60-95s/query and timed out).

function severityOf(z: PortfolioZoneDashboardDto): number {
  let s = 0;
  for (const r of z.racks) {
    if (r.status === 'grey') s += 40;
    else if (r.status === 'orange') s += 20;
    else if (r.status === 'yellow') s += 12;
  }
  for (const q of z.queues) {
    if (q.status === 'red') s += 35;
    else if (q.status === 'yellow') s += 10;
  }
  s += z.tables.filter(tb => tb.dirty).length * 4;
  s += z.tasks.overdue * 6 + z.tasks.pending * 1;
  // «Нет данных» is NOT a problem (Alexey, 2026-07-19c): dark points sink
  // below every live branch, and closed ones sink below those.
  if (!z.has_frames_today && z.is_open !== false) s -= 500;
  if (z.is_open === false) s -= 1000;
  return s;
}

// Auto-fill: as many cards per row as the viewport fits (flex-like), instead
// of fixed 2/3-column breakpoints — feedback item 4, 2026-07-19b.
const GRID = 'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(360px,1fr))] max-sm:[grid-template-columns:1fr]';

/** One ≤20-zone page: fetches its own per-unit batch; falls back to the
 *  coarse cards (data already at hand from the summary) if the batch fails. */
function DashboardChunk({
  zones, onOpenZone,
}: {
  zones: PortfolioZoneDto[];
  onOpenZone: (zoneId: string) => void;
}) {
  const ids = zones.map(z => z.zone_id);
  const { data, error } = useSWR(
    ['portfolio-dashboard', ids.join(',')],
    () => getPortfolioDashboard(ids).then(r => r.data),
    // revalidateOnFocus off: a window refocus must not flash the board back
    // to skeletons (same rationale as the shift dashboard's DASH_SWR).
    { refreshInterval: 60_000, keepPreviousData: true, revalidateOnFocus: false },
  );

  if (error && !data) {
    return (
      <>
        {zones.map(zone => (
          <ZoneHealthCard key={zone.zone_id} zone={zone} onOpen={onOpenZone} />
        ))}
      </>
    );
  }

  if (!data) {
    return (
      <>
        {zones.map(z => (
          <div key={z.zone_id} className="h-56 animate-pulse rounded-xl border border-border bg-muted/40" />
        ))}
      </>
    );
  }

  const ranked = [...data].sort((a, b) => severityOf(b) - severityOf(a));
  return (
    <>
      {ranked.map(zone => (
        <ZoneDashboardCard key={zone.zone_id} zone={zone} onOpen={onOpenZone} />
      ))}
    </>
  );
}

export function AllBranchesDashboard({ onOpenZone }: { onOpenZone: (zoneId: string) => void }) {
  const { t } = useT();
  const [pageCount, setPageCount] = useState(1);

  const { data: summary, isLoading, error } = useSWR(
    'portfolio-summary',
    () => getPortfolioSummary().then(r => r.data),
    { refreshInterval: 60_000, keepPreviousData: true, revalidateOnFocus: false },
  );

  // Board order is FROZEN while mounted (re-ranked only when the zone SET
  // changes): a background summary refresh must not reshuffle chunk
  // membership — that remounted the chunks and flashed the whole board back
  // to skeletons (the shift dashboard's calm-board lesson, applied here).
  // Группа филиалов в списке филиалов (#1969) — только её филиалы.
  const scope = useBranchScope();
  const orderRef = useRef<string[] | null>(null);
  const ranked = useMemo(() => {
    if (!summary) return undefined;
    // Same branches as the selector dropdown: service zones hidden.
    const retail = summary.filter(z => isRetailZone(z.zone_type) && (!scope.zoneIds || scope.zoneIds.has(String(z.zone_id))));
    const byId = new Map(retail.map(z => [String(z.zone_id), z]));
    const prev = orderRef.current;
    if (prev && prev.length === byId.size && prev.every(id => byId.has(id))) {
      return prev.map(id => byId.get(id)!);
    }
    const r = rankZones(retail);
    orderRef.current = r.map(z => String(z.zone_id));
    return r;
  }, [summary, scope.zoneIds]);

  if (error && !ranked) {
    return <ServerStatusNotice error={error} />;
  }

  if (isLoading && !ranked) {
    return (
      <div className={`${GRID} p-4 sm:p-6`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-56 animate-pulse rounded-xl border border-border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (!ranked || ranked.length === 0) {
    return <div className="p-6 text-sm text-muted-foreground">{t('portfolio.noZones')}</div>;
  }
  const chunks: PortfolioZoneDto[][] = [];
  for (let i = 0; i < Math.min(pageCount * ZONES_PAGE_SIZE, ranked.length); i += ZONES_PAGE_SIZE) {
    chunks.push(ranked.slice(i, i + ZONES_PAGE_SIZE));
  }
  const remaining = ranked.length - pageCount * ZONES_PAGE_SIZE;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className={GRID}>
        {chunks.map(chunk => (
          <DashboardChunk key={chunk.map(z => z.zone_id).join(',')} zones={chunk} onOpenZone={onOpenZone} />
        ))}
      </div>
      <ShowMoreZonesButton remaining={remaining} onClick={() => setPageCount(c => c + 1)} />
    </div>
  );
}
